import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";
import BrandBolt from "./BrandBolt";
import { Button } from "./ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: "/", label: "Domů" },
    { href: "/sluzby", label: "Služby" },
    { href: "/pohotovost", label: "Pohotovost 24/7", highlight: true },
    { href: "/realizace", label: "Realizace" },
    { href: "/o-nas", label: "O nás" },
    { href: "/spoluprace", label: "Spolupráce" },
    { href: "/kontakt", label: "Kontakt" },
  ];

  const isActive = (href: string) => location.pathname === href;

  // On non-home pages, always show scrolled style
  const showScrolledStyle = isScrolled || !isHomePage;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolledStyle 
          ? "bg-white/95 backdrop-blur-lg shadow-lg shadow-black/5 border-b border-border/50" 
          : "bg-transparent"
      }`}
      aria-label="Hlavní navigace"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label="EKO Servis - Elektroinstalace Teplice, domovská stránka">
            <Logo isScrolled={showScrolledStyle} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" role="menubar">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                role="menuitem"
                aria-current={isActive(link.href) ? "page" : undefined}
                className={`font-medium transition-colors relative flex items-center gap-1.5 ${
                  link.highlight 
                    ? "text-red-500 hover:text-red-600 font-bold" 
                    : showScrolledStyle 
                      ? "text-muted-foreground hover:text-foreground" 
                      : "text-white/80 hover:text-white"
                } ${isActive(link.href) ? (showScrolledStyle ? "text-foreground" : "text-white") : ""}`}
              >
                {link.highlight && <BrandBolt size="xs" variant="pulse" />}
                {link.label}
                {isActive(link.href) && (
                  <span className={`absolute -bottom-1 left-0 right-0 h-0.5 rounded-full ${link.highlight ? "bg-red-500" : "bg-primary"}`} aria-hidden="true" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <a href="tel:+420732658712" className="hidden md:block" aria-label="Zavolat na číslo +420 732 658 712">
            <Button variant="electric" size="lg" className="gap-2">
              <Phone className="h-4 w-4" aria-hidden="true" />
              +420 732 658 712
            </Button>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 ${showScrolledStyle ? "text-foreground" : "text-white"}`}
            aria-label={isMobileMenuOpen ? "Zavřít menu" : "Otevřít menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden mt-4 pb-4 animate-fade-in" role="menu">
            <div className={`flex flex-col gap-1 p-4 rounded-xl ${showScrolledStyle ? "bg-muted" : "bg-white/10 backdrop-blur-lg"}`}>
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  role="menuitem"
                  aria-current={isActive(link.href) ? "page" : undefined}
                  className={`font-medium py-3 px-4 rounded-lg transition-colors flex items-center gap-2 ${
                    link.highlight
                      ? "bg-red-500/10 text-red-500 font-bold"
                      : showScrolledStyle 
                        ? isActive(link.href) 
                          ? "bg-primary/10 text-foreground" 
                          : "text-muted-foreground hover:bg-muted hover:text-foreground" 
                        : isActive(link.href)
                          ? "bg-white/20 text-white"
                          : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {link.highlight && <BrandBolt size="sm" variant="pulse" />}
                  {link.label}
                </Link>
              ))}
              <a href="tel:+420732658712" className="mt-2" aria-label="Zavolat na číslo +420 732 658 712">
                <Button variant="electric" size="lg" className="w-full gap-2">
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Zavolat
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
